<template>
  <div class="Audits px-4">
    <v-dialog v-model="fieldSurveyPendingDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">{{ $t('FieldSurveyPending.title') }}</v-card-title>
        <v-card-text>{{ $t('FieldSurveyPending.message') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="onFieldSurveyPendingLater">{{ $t('FieldSurveyPending.completeLater') }}</v-btn>
          <v-btn
            class="text-none"
            color="#0098E1"
            dark
            @click="onFieldSurveyPendingGoToSetup"
          >
            {{ $t('FieldSurveyPending.goToSetup') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      top
      :color=snackbar.color
      rounded
      centered
    >
      {{ snackbar.text }}
    </v-snackbar>
    <v-container>
      <Header
        :auditDealsDates="combinedAuditDealsDates"
        :orgInfo="organizationData.orgInfo"
        v-on:changeAuditDeal="changeAuditDealHandler"
        :loadingQuery="$apollo.queries.organizationData.loading"
        :preferredDealKey="preferredDealKey"
        :loadingDeals="!dealsReady"
      />

      <v-divider />

      <div v-if="showAuditsMainLoading" class="display-1 text-center mt-5">
        <v-progress-circular
          indeterminate
          color="#0098E1"
        />
      </div>

      <!-- Desktop view (width >= 910px) -->
      <template v-else-if="isDesktopView">
        <v-container
          v-if="!dealsReady"
          fluid
          class="display-1 text-center mt-5"
        >
          <v-progress-circular
            indeterminate
            color="#0098E1"
          />
        </v-container>
        <v-container
          v-else-if="$apollo.queries.organizationData.loading"
          fluid
          class="display-1 text-center mt-5"
        >
          <v-progress-circular
            indeterminate
            color="#0098E1"
          />
        </v-container>
        <v-container
          v-else-if="combinedAuditDealsDates.length === 0"
          fluid
          class="display-1 text-center grey--text"
        >
          {{$t("Audits.noAudits")}}
        </v-container>
        <template v-else-if="auditDealId">
          <v-container
            v-if="shouldShowFieldSurveySetupCTA"
            fluid
            class="audits-fs-cta-wrap text-center"
          >
            <div class="audits-fs-cta-text mb-5">{{ $t('Audits.fieldSurveySetupRequired') }}</div>
            <v-btn color="#0098E1" dark depressed @click="goToSelectedFieldSurveySetup">
              {{ $t('Audits.goToFieldSurveySetup') }}
            </v-btn>
          </v-container>
          <v-container
            v-else-if="showInnerEmptyStateLoading"
            fluid
            class="display-1 text-center mt-5"
          >
            <v-progress-circular
              indeterminate
              color="#0098E1"
            />
          </v-container>
          <v-container
            v-else-if="!hasCardsToDisplay"
            fluid
            class="display-1 text-center grey--text"
          >
            {{$t("Audits.noAudits")}}
          </v-container>
          <template v-else>
            <MainCard
              v-for="device in activeKeystoneDevices"
              :key="`ks-${device.id}`"
              :id="`${device.id}-main-card`"
              :deviceData="device"
              :auditData="auditsData.find(audit => audit.device.id === device.id)"
              :flowMeasurementData="flowMeasurementsData.find(flowMeasurement => flowMeasurement.device.id === device.id)"
              :pluviometricChartData="pluviometricChartsData.find(pluviometricChart => pluviometricChart.device.id === device.id)"
              :uniformityData="uniformitiesData.find(uniformity => uniformity.device.id === device.id)"
            />

            <div v-if="showAdminEditServiceLink" class="audits-fs-edit-service-wrap">
              <a
                class="audits-fs-edit-service-link"
                href="#"
                @click.prevent="goToSelectedFieldSurveySetup"
              >
                {{ $t('FieldSurveyForm.editService') }}
              </a>
            </div>

            <FieldSurveyStatusCard
              v-for="work in visibleFieldSurveyWorks"
              :key="`fs-${work.id}`"
              :work="work"
            />
          </template>
        </template>
      </template>
      
      <!-- Mobile view (width < 910px) -->
      <v-container
        v-else
        fluid
        class="text-center mt-5 pa-5"
      >
        <v-icon size="45" color="#00202F" class="mb-4">mdi-monitor</v-icon>
        <p class="mobile-title-text mb-2">No disponible en móvil</p>
        <p class="mobile-subtitle-text">Debe acceder desde una computadora para ver este informe correctamente. La versión móvil estará disponible pronto.</p>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import Header from './Header'
import MainCard from './MainCard'
import FieldSurveyStatusCard from './FieldSurveyStatusCard'
import gql from 'graphql-tag'
import { store } from '@/store'
import { firestore, firestoreConstructors } from '@/plugins/firebase'
import {
  resolveFieldSurveyClassification,
  getAuditsAutoRedirectFieldSurveyId,
  shouldShowAuditsPendingPopup,
  FIELD_SURVEY_STATUS
} from '@/lib/fieldSurveysRouting'
import { SURVEY_WORK_STATUS } from '@/lib/surveyWorkStatus'
import {
  parseSurveyWorkUserRefId,
  fetchUsersByIds,
  formatTechnicianDisplayName
} from '@/lib/surveyWorkTechnicians'
import {
  getAuditsListQuery,
  getFlowMeasurementsListQuery,
  getPluviometricChartsListQuery,
  getUniformitiesListQuery
} from '@/lib/auditsQueries'
import { trackEvent } from '@/lib/track-events'
import { EVENTS_TO_TRACK } from '@/constants/const'

const FIELD_SURVEYS = 'app/v1/fieldSurveys'
const SURVEY_WORKS = 'app/v1/surveyWorks'

function normalizeDealKey (raw) {
  if (raw === undefined || raw === null) return ''
  return String(raw).trim()
}

function firestoreDateToJs (v) {
  if (!v) return null
  if (typeof v.toDate === 'function') return v.toDate()
  if (v instanceof Date) return v
  return null
}

function isAuditsAdminViewer (store) {
  const user = store.getters.user
  return user != null && user.isAdmin === true
}

/**
 * The organization `deals` query returns nested audits / flowMeasurements / etc. without `isPublished` filters.
 * Non-admin viewers must not see unpublished rows in deal lists or device grouping.
 */
function filterDealNestedListsByPublished (deal, store) {
  if (isAuditsAdminViewer(store)) return deal
  const onlyPublished = (items) => (items || []).filter((item) => item && item.isPublished === true)
  return {
    ...deal,
    audits: onlyPublished(deal.audits),
    flowMeasurements: onlyPublished(deal.flowMeasurements),
    pluviometricCharts: onlyPublished(deal.pluviometricCharts),
    uniformities: onlyPublished(deal.uniformities)
  }
}

export default {
  data () {
    return {
      organizationData: {auditDealsDates: []},
      auditsData: [],
      flowMeasurementsData: [],
      pluviometricChartsData: [],
      uniformitiesData: [],
      auditDealId: '',
      snackbar: {
        show: false,
        timeout: 3000,
        text: ''
      },
      windowWidth: window.innerWidth,
      devicesByDeal: [],
      fieldSurveysData: [],
      fieldSurveysLoaded: false,
      fieldSurveyWorks: [],
      fieldSurveyWorksLoading: false,
      unsubscribeFieldSurveyWorks: [],
      surveyWorksByListener: {},
      preferredDealKey: '',
      fieldSurveyPendingDialog: false,
      fieldSurveyPendingTargetId: null
    }
  },
  components: {
    Header,
    MainCard,
    FieldSurveyStatusCard
  },
  methods: {
    changeAuditDealHandler (auditDealId) {
      this.auditDealId = auditDealId
      if (this.selectedKeystoneDealId) {
        this.auditsData = []
        this.$apollo.queries.auditsData.refetch({auditDealId: this.selectedKeystoneDealId})
      } else {
        this.auditsData = []
        this.flowMeasurementsData = []
        this.pluviometricChartsData = []
        this.uniformitiesData = []
      }
    },
    handleResize () {
      this.windowWidth = window.innerWidth
    },
    async loadFieldSurveysData () {
      this.fieldSurveysLoaded = false
      const user = this.$store.getters.user
      const fieldSurveyIds = (user && user.fieldSurveyIds) || []
      if (!fieldSurveyIds.length) {
        this.fieldSurveysData = []
        this.fieldSurveysLoaded = true
        return
      }
      try {
        const db = await firestore
        const ids = [...new Set(fieldSurveyIds)]
        const snapshots = []
        while (ids.length) {
          const batch = ids.splice(0, 10)
          snapshots.push(
            db.collection(FIELD_SURVEYS)
              .where(firestoreConstructors.FieldPath.documentId(), 'in', batch)
              .get()
          )
        }
        const result = await Promise.all(snapshots)
        const parsed = []
        result.forEach((snap) => {
          snap.forEach((doc) => {
            const d = doc.data() || {}
            const dealKey = normalizeDealKey(d.dealId)
            if (!dealKey) return
            const createdAtDate = firestoreDateToJs(d.createdAt)
            parsed.push({
              id: doc.id,
              dealKey,
              createdAt: createdAtDate ? createdAtDate.toISOString() : null,
              status: d.status != null ? String(d.status) : 'new'
            })
          })
        })
        this.fieldSurveysData = parsed
        this.fieldSurveysLoaded = true
      } catch (e) {
        console.error('Field surveys load failed:', e)
        this.fieldSurveysData = []
        this.fieldSurveysLoaded = true
      }
    },
    teardownSurveyWorksListeners () {
      this.unsubscribeFieldSurveyWorks.forEach((fn) => {
        if (typeof fn === 'function') fn()
      })
      this.unsubscribeFieldSurveyWorks = []
      this.surveyWorksByListener = {}
      this.fieldSurveyWorks = []
      this.fieldSurveyWorksLoading = false
    },
    async enrichAndStoreSurveyWorks () {
      const all = Object.values(this.surveyWorksByListener).flat()
      const byId = new Map()
      all.forEach((work) => byId.set(work.id, work))
      const list = [...byId.values()]
      list.sort((a, b) => {
        const ta = firestoreDateToJs(a.createdAt)
        const tb = firestoreDateToJs(b.createdAt)
        if (ta && tb) return ta.getTime() - tb.getTime()
        if (ta && !tb) return -1
        if (!ta && tb) return 1
        return a.id.localeCompare(b.id)
      })
      const userIds = list.filter((w) => w.userId && !w.ponceTechnician).map((w) => w.userId)
      list.forEach((w) => {
        if (w.ponceTechnician) {
          w.userDisplay = this.$t('FieldSurveyForm.ponceTechnicianOption')
        }
      })
      if (userIds.length) {
        try {
          const db = await firestore
          const users = await fetchUsersByIds(db, userIds)
          list.forEach((w) => {
            if (w.ponceTechnician || !w.userId) return
            if (!users.has(w.userId)) {
              w.userDisplay = w.userId
              return
            }
            const userData = users.get(w.userId)
            w.userDisplay = formatTechnicianDisplayName(userData.name, userData.lastName)
          })
        } catch (e) {
          console.error('Survey works enrich failed:', e)
        }
      }
      this.fieldSurveyWorks = list
      this.fieldSurveyWorksLoading = false
    },
    async listenSurveyWorksBySelectedDeal () {
      this.teardownSurveyWorksListeners()
      if (!this.selectedDealKey) return
      if (this.shouldBlockSurveyWorksByFieldSurveyStatus) return
      const relatedFieldSurveys = this.fieldSurveysData.filter((item) => item.dealKey === this.selectedDealKey)
      if (!relatedFieldSurveys.length) return
      this.fieldSurveyWorksLoading = true
      try {
        const db = await firestore
        const refs = relatedFieldSurveys.map((item) => db.collection(FIELD_SURVEYS).doc(item.id))
        while (refs.length) {
          const batchRefs = refs.splice(0, 10)
          const listenerIndex = this.unsubscribeFieldSurveyWorks.length
          const unsubscribe = db.collection(SURVEY_WORKS).where('fieldSurvey', 'in', batchRefs).onSnapshot(
            (querySnap) => {
              const items = []
              querySnap.forEach((doc) => {
                const d = doc.data() || {}
                const ponceTechnician = !!d.ponceTechnician
                const userId = ponceTechnician ? '' : (parseSurveyWorkUserRefId(d.user) || '')
                items.push({
                  id: doc.id,
                  deviceName: d.deviceName != null ? String(d.deviceName) : (d.name != null ? String(d.name) : ''),
                  userId,
                  ponceTechnician,
                  userDisplay: '',
                  coordinates: d.coordinates != null ? String(d.coordinates) : '',
                  hasPluviometricChart: !!(d.pluviometricChart && String(d.pluviometricChart).trim()),
                  status: d.status != null ? String(d.status) : SURVEY_WORK_STATUS.NEW,
                  auditId: d.auditId != null ? String(d.auditId) : '',
                  createdAt: d.createdAt
                })
              })
              this.$set(this.surveyWorksByListener, listenerIndex, items)
              this.enrichAndStoreSurveyWorks()
            },
            (err) => {
              console.error('Survey works listener error:', err)
              this.fieldSurveyWorksLoading = false
            }
          )
          this.unsubscribeFieldSurveyWorks.push(unsubscribe)
        }
      } catch (e) {
        console.error('Survey works setup failed:', e)
        this.fieldSurveyWorksLoading = false
      }
    },
    async runFieldSurveyEntryFlow () {
      if (this.$store.getters.impersonatedUserId) return
      const user = this.$store.getters.user
      const fieldSurveyIds = (user && user.fieldSurveyIds) || []
      if (!fieldSurveyIds.length) return
      try {
        const db = await firestore
        const classification = await resolveFieldSurveyClassification(db, fieldSurveyIds)
        const redirectId = getAuditsAutoRedirectFieldSurveyId(classification)
        if (redirectId) {
          trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_AUTO_REDIRECT_FROM_AUDITS, { fieldSurveyId: redirectId })
          this.$router.replace({ path: `/fieldSurveys/${redirectId}` })
          return
        }
        if (shouldShowAuditsPendingPopup(classification)) {
          this.fieldSurveyPendingTargetId = classification.oldestPendingId
          this.fieldSurveyPendingDialog = true
          trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_PENDING_DIALOG_SHOWN, {
            fieldSurveyId: classification.oldestPendingId
          })
        }
      } catch (e) {
        console.error('Field survey routing check failed:', e)
      }
    },
    onFieldSurveyPendingLater () {
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_PENDING_COMPLETE_LATER, {
        fieldSurveyId: this.fieldSurveyPendingTargetId || ''
      })
      this.fieldSurveyPendingDialog = false
    },
    onFieldSurveyPendingGoToSetup () {
      if (!this.fieldSurveyPendingTargetId) return
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_PENDING_GO_TO_SETUP, {
        fieldSurveyId: this.fieldSurveyPendingTargetId
      })
      this.fieldSurveyPendingDialog = false
      this.$router.push({ path: `/fieldSurveys/${this.fieldSurveyPendingTargetId}` })
    },
    goToSelectedFieldSurveySetup () {
      if (!this.selectedFieldSurvey || !this.selectedFieldSurvey.id) return
      trackEvent(EVENTS_TO_TRACK.FIELD_SURVEY_CTA_GO_TO_SETUP, {
        fieldSurveyId: this.selectedFieldSurvey.id,
        dealKey: this.selectedDealKey || '',
        fromAuditsAdminEdit: this.showAdminEditServiceLink
      })
      this.$router.push({ path: `/fieldSurveys/${this.selectedFieldSurvey.id}` })
    },
    mergeFieldSurveyRecord (record) {
      if (!record || !record.id || !record.dealKey) return
      const idx = this.fieldSurveysData.findIndex((item) => item.id === record.id)
      if (idx >= 0) {
        this.$set(this.fieldSurveysData, idx, record)
      } else {
        this.fieldSurveysData.push(record)
      }
    },
    async loadAdminFieldSurveyForDeal (dealKey) {
      const key = normalizeDealKey(dealKey)
      if (!key || !this.isPonceAdmin) return
      if (this.fieldSurveysData.some((item) => item.dealKey === key)) return
      try {
        const db = await firestore
        let snap = await db.collection(FIELD_SURVEYS).where('dealId', '==', key).get()
        if (snap.empty && /^\d+$/.test(key)) {
          snap = await db.collection(FIELD_SURVEYS).where('dealId', '==', Number(key)).get()
        }
        if (snap.empty) return
        let best = null
        snap.forEach((doc) => {
          const d = doc.data() || {}
          const createdAtDate = firestoreDateToJs(d.createdAt)
          const record = {
            id: doc.id,
            dealKey: key,
            createdAt: createdAtDate ? createdAtDate.toISOString() : null,
            status: d.status != null ? String(d.status) : FIELD_SURVEY_STATUS.NEW
          }
          if (!best) {
            best = record
            return
          }
          const bestMs = new Date(best.createdAt || 0).getTime()
          const recordMs = new Date(record.createdAt || 0).getTime()
          if (recordMs >= bestMs) best = record
        })
        if (best) this.mergeFieldSurveyRecord(best)
      } catch (e) {
        console.error('Admin field survey lookup failed:', e)
      }
    },
    setDevicesByDeal () {
      this.devicesByDeal = (this.organizationData.auditDealsDates || []).map(deal => {
        // Create a map to group by device.id
        const devicesMap = new Map()
        
        // Group audits by device.id
        if (deal.audits) {
          deal.audits.forEach(audit => {
            if (audit.device && audit.device.id) {
              const deviceId = audit.device.id
              if (!devicesMap.has(deviceId)) {
                devicesMap.set(deviceId, {
                  id: deviceId,
                  name: audit.device.name,
                  audits: [],
                  flowMeasurements: [],
                  pluviometricCharts: [],
                  uniformitys: []
                })
              }
              devicesMap.get(deviceId).audits.push(audit)
            }
          })
        }
        
        // Group flowMeasurements by device.id
        if (deal.flowMeasurements) {
          deal.flowMeasurements.forEach(flowMeasurement => {
            if (flowMeasurement.device && flowMeasurement.device.id) {
              const deviceId = flowMeasurement.device.id
              if (!devicesMap.has(deviceId)) {
                devicesMap.set(deviceId, {
                  id: deviceId,
                  name: flowMeasurement.device.name,
                  audits: [],
                  flowMeasurements: [],
                  pluviometricCharts: [],
                  uniformitys: []
                })
              }
              devicesMap.get(deviceId).flowMeasurements.push(flowMeasurement)
            }
          })
        }
        
        // Group pluviometricCharts by device.id
        if (deal.pluviometricCharts) {
          deal.pluviometricCharts.forEach(pluviometricChart => {
            if (pluviometricChart.device && pluviometricChart.device.id) {
              const deviceId = pluviometricChart.device.id
              if (!devicesMap.has(deviceId)) {
                devicesMap.set(deviceId, {
                  id: deviceId,
                  name: pluviometricChart.device.name,
                  audits: [],
                  flowMeasurements: [],
                  pluviometricCharts: [],
                  uniformitys: []
                })
              }
              devicesMap.get(deviceId).pluviometricCharts.push(pluviometricChart)
            }
          })
        }
        
        // Group uniformities by device.id
        if (deal.uniformities) {
          deal.uniformities.forEach(uniformity => {
            if (uniformity.device && uniformity.device.id) {
              const deviceId = uniformity.device.id
              if (!devicesMap.has(deviceId)) {
                devicesMap.set(deviceId, {
                  id: deviceId,
                  name: uniformity.device.name,
                  audits: [],
                  flowMeasurements: [],
                  pluviometricCharts: [],
                  uniformitys: []
                })
              }
              devicesMap.get(deviceId).uniformitys.push(uniformity)
            }
          })
        }
        
        // Return deal with devices array
        return {
          id: deal.id,
          devices: Array.from(devicesMap.values())
        }
      })
    }
  },
  computed: {
    organizationHubspotId () {
      return this.$store.getters.selectedOrganization.hubspotId
    },
    combinedAuditDealsDates () {
      const keystoneDeals = (this.organizationData.auditDealsDates || []).map((deal) => ({
        ...deal,
        audits: deal.audits || [],
        flowMeasurements: deal.flowMeasurements || [],
        pluviometricCharts: deal.pluviometricCharts || [],
        uniformities: deal.uniformities || [],
        _dealKey: normalizeDealKey(deal.hubspotId),
        _isFieldSurveyOnly: false
      }))
      const dealKeySet = new Set(keystoneDeals.map((deal) => normalizeDealKey(deal.hubspotId)))
      const fieldSurveyOnlyDeals = []
      this.fieldSurveysData.forEach((fieldSurvey) => {
        if (!fieldSurvey.dealKey || dealKeySet.has(fieldSurvey.dealKey)) return
        dealKeySet.add(fieldSurvey.dealKey)
        fieldSurveyOnlyDeals.push({
          id: `fs-${fieldSurvey.dealKey}`,
          hubspotId: fieldSurvey.dealKey,
          createdAt: fieldSurvey.createdAt || new Date(0).toISOString(),
          audits: [],
          flowMeasurements: [],
          pluviometricCharts: [],
          uniformities: [],
          _dealKey: fieldSurvey.dealKey,
          _isFieldSurveyOnly: true
        })
      })
      return [...keystoneDeals, ...fieldSurveyOnlyDeals].sort((a, b) => {
        const ta = new Date(a.createdAt).getTime()
        const tb = new Date(b.createdAt).getTime()
        return tb - ta
      })
    },
    selectedDealData () {
      return this.combinedAuditDealsDates.find((deal) => deal.id === this.auditDealId) || null
    },
    selectedDealKey () {
      if (!this.selectedDealData) return ''
      return this.selectedDealData._dealKey || normalizeDealKey(this.selectedDealData.hubspotId)
    },
    selectedKeystoneDealId () {
      if (!this.selectedDealData || this.selectedDealData._isFieldSurveyOnly) return ''
      return this.selectedDealData.id
    },
    selectedFieldSurvey () {
      if (!this.selectedDealKey) return null
      const candidates = this.fieldSurveysData.filter((item) => item.dealKey === this.selectedDealKey)
      if (!candidates.length) return null
      const sorted = [...candidates].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime()
        const tb = new Date(b.createdAt || 0).getTime()
        return tb - ta
      })
      return sorted[0]
    },
    shouldBlockSurveyWorksByFieldSurveyStatus () {
      if (!this.selectedFieldSurvey) return false
      return this.selectedFieldSurvey.status === 'new' || this.selectedFieldSurvey.status === 'inProgress'
    },
    shouldShowFieldSurveySetupCTA () {
      return this.shouldBlockSurveyWorksByFieldSurveyStatus
    },
    activeKeystoneDevices () {
      const deal = this.devicesByDeal.find((item) => item.id === this.selectedKeystoneDealId)
      return deal ? deal.devices : []
    },
    visibleFieldSurveyWorks () {
      if (this.shouldBlockSurveyWorksByFieldSurveyStatus) return []
      const keystoneAuditIds = new Set(this.auditsData.map((audit) => audit.id))
      return this.fieldSurveyWorks.filter((work) => !work.auditId || !keystoneAuditIds.has(work.auditId))
    },
    hasCardsToDisplay () {
      return this.activeKeystoneDevices.length > 0 || this.visibleFieldSurveyWorks.length > 0
    },
    /**
     * Header sets auditDealId asynchronously after deals load; avoid empty / wrong states in between.
     */
    isWaitingForHeaderDealSelection () {
      return this.dealsReady &&
        this.combinedAuditDealsDates.length > 0 &&
        !this.auditDealId
    },
    isKeystoneDetailQueriesLoading () {
      if (!this.selectedKeystoneDealId) return false
      const q = this.$apollo.queries
      if (!q) return false
      return Boolean(
        (q.auditsData && q.auditsData.loading) ||
        (q.flowMeasurementsData && q.flowMeasurementsData.loading) ||
        (q.pluviometricChartsData && q.pluviometricChartsData.loading) ||
        (q.uniformitiesData && q.uniformitiesData.loading)
      )
    },
    showAuditsMainLoading () {
      if (!this.isDesktopView) {
        const q = this.$apollo.queries
        return !!(q && q.auditsData && q.auditsData.loading)
      }
      if (this.isWaitingForHeaderDealSelection) return true
      if (this.selectedKeystoneDealId && this.isKeystoneDetailQueriesLoading) return true
      const q = this.$apollo.queries
      return !!(q && q.auditsData && q.auditsData.loading)
    },
    /**
     * Empty layout but detail queries or field survey works still loading — do not flash "no audits".
     */
    showInnerEmptyStateLoading () {
      if (!this.auditDealId || this.hasCardsToDisplay || this.shouldShowFieldSurveySetupCTA) return false
      if (this.fieldSurveyWorksLoading) return true
      if (this.selectedKeystoneDealId && this.isKeystoneDetailQueriesLoading) return true
      return false
    },
    dealsReady () {
      return !this.$apollo.queries.organizationData.loading && this.fieldSurveysLoaded
    },
    isDesktopView () {
      return this.windowWidth >= 910
    },
    isPonceAdmin () {
      if (this.$store.getters.impersonatedUserId) return true
      return isAuditsAdminViewer(this.$store)
    },
    showAdminEditServiceLink () {
      if (!this.isPonceAdmin || this.shouldShowFieldSurveySetupCTA) return false
      const fs = this.selectedFieldSurvey
      if (!fs || !fs.id) return false
      return fs.status === FIELD_SURVEY_STATUS.COMPLETED
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
    const queryDealId = String((this.$route.query && this.$route.query.dealId) || '').trim()
    this.preferredDealKey = queryDealId
    this.runFieldSurveyEntryFlow()
    this.loadFieldSurveysData()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    this.teardownSurveyWorksListeners()
  },
  apollo: {
    $client: 'auditsClient',
    organizationData () {
      return {
        query: gql`query getAuditDealsDates($organizationHubspotId: String!) {
          deals(
            where: {
              organization: {
                hubspotId: {
                  equals: $organizationHubspotId
                }
              }
            },
            orderBy: [{
              createdAt: desc
            }]
          ){
            id
            audits {
              isPublished
              device {
                id
                name
              }
            }
            flowMeasurements {
              isPublished
              device {
                id
                name
              }
            }
            pluviometricCharts {
              isPublished
              device {
                id
                name
              }
            }
            uniformities {
              isPublished
              device {
                id
                name
              }
            }
            hubspotId
            createdAt
          }
        }`,
        update: data => {
          const deals = (data.deals || []).map((deal) =>
            filterDealNestedListsByPublished(deal, store)
          )
          return {
            auditDealsDates: deals
          }
        },
        skip () {
          return !this.organizationHubspotId
        },
        variables () {
          return {
            organizationHubspotId: this.organizationHubspotId
          }
        }
      }
    },
    auditsData () {
      return {
        query () {
          const publishedOnly = !isAuditsAdminViewer(this.$store)
          return getAuditsListQuery({ includeDealFilter: true, publishedOnly })
        },
        update: data => {
          return data.audits
        },
        skip () {
          return !this.selectedKeystoneDealId
        },
        variables () {
          return {
            auditDealId: this.selectedKeystoneDealId
          }
        }
      }
    },
    flowMeasurementsData () {
      return {
        query () {
          const publishedOnly = !isAuditsAdminViewer(this.$store)
          return getFlowMeasurementsListQuery({ includeDealFilter: true, publishedOnly })
        },
        update: data => {
          return data.flowMeasurements
        },
        skip () {
          return !this.selectedKeystoneDealId
        },
        variables () {
          return {
            auditDealId: this.selectedKeystoneDealId
          }
        }
      }
    },
    pluviometricChartsData () {
      return {
        query () {
          const publishedOnly = !isAuditsAdminViewer(this.$store)
          return getPluviometricChartsListQuery({ includeDealFilter: true, publishedOnly })
        },
        update: data => {
          return data.pluviometricCharts
        },
        skip () {
          return !this.selectedKeystoneDealId
        },
        variables () {
          return {
            auditDealId: this.selectedKeystoneDealId
          }
        }
      }
    },
    uniformitiesData () {
      return {
        query () {
          const publishedOnly = !isAuditsAdminViewer(this.$store)
          return getUniformitiesListQuery({ includeDealFilter: true, publishedOnly })
        },
        update: data => {
          return data.uniformities
        },
        skip () {
          return !this.selectedKeystoneDealId
        },
        variables () {
          return {
            auditDealId: this.selectedKeystoneDealId
          }
        }
      }
    }
  },
  watch: {
    'organizationData.auditDealsDates': {
      handler () {
        this.setDevicesByDeal()
      },
      deep: true
    },
    auditDealId: {
      handler () {
        this.listenSurveyWorksBySelectedDeal()
      }
    },
    selectedDealKey: {
      immediate: true,
      handler (dealKey) {
        if (dealKey) this.loadAdminFieldSurveyForDeal(dealKey)
      }
    },
    fieldSurveysData: {
      handler () {
        this.listenSurveyWorksBySelectedDeal()
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.mobile-title-text {
  font-size: 1.38rem;
  font-weight: 400;
  color: #00202F;
}

.mobile-subtitle-text {
  font-size: 1.12rem;
  font-weight: 300;
  color: #00202F;
}

.audits-fs-cta-wrap {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.audits-fs-cta-text {
  color: #5f6d75;
  font-size: 2.2rem;
  font-weight: 300;
}

.audits-fs-edit-service-wrap {
  display: flex;
  justify-content: flex-end;
  margin: 8px 12px 12px 0;
}

.audits-fs-edit-service-link {
  color: #0098E1;
  font-weight: 500;
  font-size: 0.9375rem;
  text-decoration: none;
  white-space: nowrap;
}

.audits-fs-edit-service-link:hover {
  text-decoration: underline;
}

</style>


// WEBPACK FOOTER //
// Audits.vue?9d9eda76